import type { paths } from "@frend-digital/centra-types/oas";
import { events } from "@frend-digital/events/store";
import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { type ParamsOption, type RequestBodyOption } from "openapi-fetch";
import { useCallback } from "react";
import { useSetLocalToken } from "../auth/store";
import { useCentraClient, useCentraContext } from "../context";
import { optimisticUpdateQuantity } from "../selection/optimisticUpdate";
import { useSelectionKey } from "../useSelectionKey";

export type Selection =
  paths["/selection"]["get"]["responses"]["200"]["content"]["application/json"];

export type SelectionOptions<T, U> = {
  select?: (data: U) => T;
};

export const useSelection = <T = Selection>({
  select,
}: SelectionOptions<T, Selection> = {}) => {
  const setToken = useSetLocalToken();
  const key = useSelectionKey();
  const { client, waitingForToken } = useCentraContext();

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await client.GET("/selection");
      if (error) throw error;

      if (data.token) {
        setToken(data.token);
      }

      return data;
    },
    select,
    enabled: !waitingForToken,
  });
};

interface MutationContext {
  oldData?: Selection;
}

type MutationOptions<
  TData,
  TVars = unknown,
  TContext = MutationContext,
> = Partial<UseMutationOptions<TData, Error, TVars, TContext>>;

interface AddToCartVars {
  itemId: string;
}

export const useAddToCart = (
  options: MutationOptions<Selection, AddToCartVars> = {},
) => {
  const key = useSelectionKey();
  const client = useCentraClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationKey: ["addToCart", key],
    mutationFn: async ({ itemId }: AddToCartVars) => {
      const { data, error } = await client.POST("/items/{item}", {
        params: {
          path: {
            item: itemId,
          },
        },
      });

      if (error) throw error;

      return data as Selection;
    },
    onMutate: async (...args) => {
      queryClient.cancelQueries({ queryKey: key });
      return options?.onMutate?.(...args);
    },
    onSuccess: (...args) => {
      const [data, { itemId }] = args;
      queryClient.setQueryData<Selection>(key, data);

      events.dispatch("add_to_cart", data, {
        dispatcher: "centra",
        item_id: itemId,
      });

      return options?.onSuccess?.(...args);
    },
  });
};

export const useSetSelection = () => {
  const selectionKey = useSelectionKey();
  const queryClient = useQueryClient();

  return useCallback(
    (
      selection: Selection,
      { invalidate = false }: { invalidate?: boolean } = {},
    ) => {
      queryClient.setQueryData(selectionKey, selection);
      if (invalidate) queryClient.invalidateQueries({ queryKey: selectionKey });
    },
    [selectionKey],
  );
};

interface SetQuantityVars {
  line: string;
  quantity: number;
}

export const useSetQuantity = ({
  optimistic = true,
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: MutationOptions<Selection, SetQuantityVars> & {
  optimistic?: boolean;
} = {}) => {
  const selectionKey = useSelectionKey();
  const queryClient = useQueryClient();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["setQuantity", selectionKey],
    mutationFn: async ({ line, quantity }: SetQuantityVars) => {
      const { data, error } = await client.PUT(
        "/lines/{line}/quantity/{quantity}",
        {
          params: {
            path: {
              line,
              quantity: String(quantity),
            },
          },
        },
      );

      if (error) throw error;

      return data as Selection;
    },

    onMutate: async ({ line, quantity }) => {
      if (!optimistic) return onMutate?.({ line, quantity });
      queryClient.cancelQueries({ queryKey: selectionKey });
      queryClient.setQueryData(
        selectionKey,
        (oldData: Selection | undefined) => {
          const rawSelection = oldData;
          if (!rawSelection) return oldData;

          const updatedSelection = optimisticUpdateQuantity(rawSelection, {
            line,
            newQuantity: quantity,
          });

          return updatedSelection;
        },
      );

      return onMutate?.({ line, quantity });
    },
    onError,
    onSuccess,
    onSettled: (...args) => {
      queryClient.invalidateQueries({ queryKey: selectionKey });
      onSettled?.(...args);
    },
  });
};

interface RemoveFromCartVars {
  line: string;
  optimistic?: boolean;
}

export const useRemoveFromCart = (
  options: MutationOptions<Selection, RemoveFromCartVars> = {},
  optimistic = true,
) => {
  const setQuantity = useSetQuantity({ optimistic });
  const selectionKey = useSelectionKey();

  return useMutation({
    mutationKey: ["removeFromCart", selectionKey],
    mutationFn: ({ line }: { line: string }) => {
      return setQuantity.mutateAsync({ line, quantity: 0 });
    },
    ...options,

    onSuccess: (...args) => {
      const [data] = args;
      events.dispatch("remove_from_cart", data, { dispatcher: "centra" });
      return options?.onSuccess?.(...args);
    },
  });
};

type LoginVars = {
  email: string;
  password: string;
};

export const useLogin = (options?: MutationOptions<Selection, LoginVars>) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["login", selectionKey],
    mutationFn: async ({ email, password }: LoginVars) => {
      const { data, error } = await client.POST("/login/{email}", {
        params: {
          path: {
            email,
          },
        },
        body: {
          password,
        },
      });

      if (error) throw error;

      return data;
    },
    ...options,
    onMutate: async arg0 => {
      queryClient.cancelQueries({ queryKey: selectionKey });
      return options?.onMutate?.(arg0);
    },
    onSuccess: (...args) => {
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
      return options?.onSuccess?.(...args);
    },
    onSettled: (...args) => {
      queryClient.invalidateQueries({ queryKey: selectionKey });

      return options?.onSettled?.(...args);
    },
  });
};

export const useLogout = ({
  onMutate,
  onError,
  onSuccess,
  onSettled,
}: MutationOptions<Selection, never> = {}) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["logout", selectionKey],
    mutationFn: async (_?: never) => {
      const { data, error } = await client.POST("/logout");
      if (error) throw error;

      return data;
    },
    onSuccess,
    onMutate: async (...args) => {
      queryClient.cancelQueries({ queryKey: selectionKey });
      const oldData = queryClient.getQueryData<Selection>(selectionKey);

      // Remove selection.rawSelection.loggedIn
      if (oldData) {
        queryClient.setQueryData(
          selectionKey,
          (oldData: Selection | undefined) => {
            const rawSelection = oldData;
            if (!rawSelection) return oldData;

            const updatedSelection = {
              ...rawSelection,
              loggedIn: undefined,
            };

            return updatedSelection;
          },
        );
      }

      await onMutate?.(...args);

      return {
        oldData: oldData!,
      };
    },
    onError: (error, variables, context) => {
      context?.oldData &&
        queryClient.setQueryData(selectionKey, context?.oldData);
      return onError?.(error, variables, context);
    },
    onSettled: (...args) => {
      queryClient.invalidateQueries({ queryKey: selectionKey });
      return onSettled?.(...args);
    },
  });
};

type RegisterVars = RequestBodyOption<paths["/register"]["post"]>["body"];
export const useRegister = (
  options?: MutationOptions<Selection, RegisterVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["signup", selectionKey],
    mutationFn: async (variables: RegisterVars) => {
      const { data, error } = await client.POST("/register", {
        body: variables,
      });

      if (error) throw error;
      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);

      events.dispatch("sign_up", data, { dispatcher: "centra" });
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

type UserOptions = RequestBodyOption<paths["/customer/update"]["put"]>["body"];

export const useUpdateUser = (
  options?: MutationOptions<Selection, UserOptions>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["updateUser", selectionKey],
    mutationFn: async (variables: UserOptions) => {
      const { data, error } = await client.PUT("/customer/update", {
        body: variables,
      });

      if (error) throw error;

      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};
type ChangePasswordVars = RequestBodyOption<paths["/password"]["put"]>["body"];

/**
 * Change the password for the current logged in user
 */
export const useChangePassword = (
  options?: MutationOptions<Selection, ChangePasswordVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["changePassword", selectionKey],
    mutationFn: async (body: ChangePasswordVars) => {
      const { data, error } = await client.PUT("/password", {
        body,
      });

      if (error) throw error;

      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

type ResetPasswordVars = RequestBodyOption<
  paths["/password-reset"]["post"]
>["body"];

export const useResetPassword = (
  options?: MutationOptions<Selection, ResetPasswordVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["resetPassword", selectionKey],
    mutationFn: async (body: ResetPasswordVars) => {
      const { data, error } = await client.POST("/password-reset", {
        body,
      });

      if (error) throw error;

      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

type ResetPasswordEmailVars = RequestBodyOption<
  paths["/password-reset-email/{email}"]["post"]
>["body"] & {} & ParamsOption<
    paths["/password-reset-email/{email}"]["post"]
  >["params"]["path"] & {};

export const useResetPasswordEmail = (
  options?: MutationOptions<Selection, ResetPasswordEmailVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["resetPasswordEmail", selectionKey],
    mutationFn: async ({ email, ...body }: ResetPasswordEmailVars) => {
      const { data, error } = await client.POST(
        "/password-reset-email/{email}",
        {
          params: {
            path: {
              email,
            },
          },
          body: body,
        },
      );

      if (error) throw error;

      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

type SetCountryVars = RequestBodyOption<
  paths["/countries/{country}"]["put"]
>["body"] &
  ParamsOption<paths["/countries/{country}"]["put"]>["params"]["path"];

export const useSetCountry = (
  options?: MutationOptions<Selection, SetCountryVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["setCountry", selectionKey],
    mutationFn: async ({ country, ...body }: SetCountryVars) => {
      const { data, error } = await client.PUT("/countries/{country}", {
        params: {
          path: {
            country,
          },
        },
        body,
      });

      if (error) throw error;

      return data;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

type SubscribeVars = RequestBodyOption<
  paths["/newsletter-subscription"]["post"]
>["body"];

export const useSubscribe = (
  options?: MutationOptions<Selection, SubscribeVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["newsletter", selectionKey],
    mutationFn: async (variables: SubscribeVars) => {
      const { data, error } = await client.POST("/newsletter-subscription", {
        body: variables,
      });

      if (error) throw error;

      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

export const useSubscriptions = () => {
  const client = useCentraClient();
  const selectionKey = useSelectionKey();

  return useQuery({
    queryKey: ["subscriptions", selectionKey],
    queryFn: async () => {
      const { data, error } = await client.POST("/customer/subscriptions");

      if (error) throw error;

      return data as unknown as Selection;
    },
  });
};

type AddVoucherVars = RequestBodyOption<paths["/vouchers"]["post"]>["body"];

export const useAddVoucher = (
  options?: MutationOptions<Selection, AddVoucherVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["addVoucher", selectionKey],
    mutationFn: async (body: AddVoucherVars) => {
      const { data, error } = await client.POST("/vouchers", {
        body,
      });

      if (error) throw error;

      return data;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

export const useRemoveVoucher = (
  options?: MutationOptions<Selection, AddVoucherVars>,
) => {
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();
  const client = useCentraClient();

  return useMutation({
    mutationKey: ["addVoucher", selectionKey],
    mutationFn: async (body: AddVoucherVars) => {
      const { data, error } = await client.DELETE("/vouchers", {
        body,
      });

      if (error) throw error;

      return data;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

/**
 * Back in stock subscription
 */

type BackInStockVars = RequestBodyOption<
  paths["/back-in-stock-subscription"]["post"]
>["body"];
export const useBackInStock = (
  options?: MutationOptions<Selection, BackInStockVars>,
) => {
  const client = useCentraClient();
  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();

  return useMutation({
    mutationKey: ["backInStock", selectionKey],
    mutationFn: async (variables: BackInStockVars) => {
      const { data, error } = await client.POST("/back-in-stock-subscription", {
        body: variables,
      });

      if (error) throw error;

      return data as unknown as Selection;
    },
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [data] = args;
      queryClient.setQueryData<Selection>(selectionKey, data);
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: selectionKey });
    },
  });
};

export const useSelectionStatus = () => {
  const selectionKey = useSelectionKey();

  const selectionFetching = useIsFetching({
    queryKey: selectionKey,
  });

  const quantityPending = useIsMutating({
    mutationKey: ["setQuantity", selectionKey],
  });
  const addToCartPending = useIsMutating({
    mutationKey: ["addToCart", selectionKey],
  });

  const isPending =
    !!selectionFetching || !!quantityPending || !!addToCartPending;

  return {
    /**
     * True when any optimistic mutation is pending or when selection is fetching
     */
    isPending,
  };
};

const _selectLoggedIn = (data: Selection) => !!data.loggedIn;
export const useLoggedIn = () => useSelection({ select: _selectLoggedIn });

const _selectUser = (data: Selection) => data.loggedIn;
export const useUser = () => useSelection({ select: _selectUser });

const _selectCheckoutScript = (data: Selection) =>
  data.selection?.centraCheckoutScript;
export const useCentraCheckoutScript = () =>
  useSelection({ select: _selectCheckoutScript });
